import { IconExternalLink, IconGlobe, IconSize } from '@amzn/stencil-react-components/icons';
import { Row } from '@amzn/stencil-react-components/layout';
import { MessageBanner, MessageBannerLink } from '@amzn/stencil-react-components/message-banner';
import { PageHeader, PageHeaderDropdown, PageHeaderDropdownButton } from '@amzn/stencil-react-components/page';
import { Button } from '@amzn/stencil-react-components/button';
import { H1 } from '@amzn/stencil-react-components/text';
import React from 'react';
import useArb, { Locale } from '../../hooks/useArb';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useSessionStorage } from '../../hooks/useSessionStorage';
import { getAgencyUrl } from '../../Utils/AgencyUrlGenerator';
import constants from '../../Utils/Constants';
import {
  DEFAULT_SUPPORTED_LANGUAGES,
  getGrievanceChatbotUrl,
  getLanguageOptions,
  isMENACountry,
  LANGUAGES,
} from '../../Utils/CountryConstant';

interface HeaderTabProps {
  onSignOut?: () => void;
}

export default function HeaderTab({ onSignOut }: HeaderTabProps) {
  const { t, locale, setLocale } = useArb('appStrings');
  const [countryCode] = useSessionStorage('countryCode', 'other', { parseJSON: false });
  const [supportedLanguages] = useLocalStorage(
    constants.FEATURE_FLAGS.SUPPORTED_LANGUAGES,
    DEFAULT_SUPPORTED_LANGUAGES,
  );
  const languageOptions = getLanguageOptions(countryCode, supportedLanguages);
  const [grievanceFeatureFlag] = useLocalStorage(constants.FEATURE_FLAGS.GRIEVANCE_REPORTING, false);
  const [isHBADeploymentGroup] = useSessionStorage(constants.FEATURE_FLAGS.HBA_DEPLOYMENT_GROUP, false);
  const [agencyName] = useSessionStorage(constants.FEATURE_FLAGS.AGENCY_NAME, '', { parseJSON: false });
  const [stage] = useSessionStorage(constants.SPECTROMETER.STAGE, '', { parseJSON: false });

  return (
    <React.Fragment>
      {/* App Header */}
      <PageHeader data-testid="page-app-header-tab" isFixed={true} hasShadow={false}>
        <Row alignItems="center" justifyContent="space-between" flex="1">
          <H1 fontSize="T400" fontWeight="medium" color="white" textAlign="center">
            {t('PHO-application-title', 'Pre-hire orientation')}
          </H1>

          <Row alignItems="center" style={{ gap: '16px' }}>
            <PageHeaderDropdown
              data-testid="page-app-header-tab-dropdown"
              icon={<IconGlobe title="Change Language" />}
              items={languageOptions.map((item) => (
                <PageHeaderDropdownButton
                  data-testid="page-app-header-tab-dropdown-button"
                  onClick={() => setLocale(item?.shortName as Locale)}
                >
                  {item?.longName}
                </PageHeaderDropdownButton>
              ))}
            >
              {LANGUAGES[locale]?.longName}
            </PageHeaderDropdown>

            {onSignOut && (
              <Button data-testid="signout-button" onClick={onSignOut}>
                {t('PHO-sign-out', 'Sign Out')}
              </Button>
            )}
          </Row>
        </Row>
      </PageHeader>

      {/* SR Grievance Banner */}
      {(isMENACountry() || isHBADeploymentGroup) && grievanceFeatureFlag && (
        <MessageBanner data-testid="sr-grievance-message-banner">
          {t(
            'PHO-grievance',
            'You deserve fair and equal recruitment practices. However, if you experience a violation during the hiring process, report an issue',
            {
              a: (chunk: any) => (
                <MessageBannerLink
                  data-testid="sr-grievance-message-banner-link"
                  href={isHBADeploymentGroup ? getAgencyUrl(stage, agencyName, locale) : getGrievanceChatbotUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  icon={<IconExternalLink size={IconSize.ExtraSmall} />}
                >
                  {chunk}
                </MessageBannerLink>
              ),
            },
          )}
        </MessageBanner>
      )}
    </React.Fragment>
  );
}
